import { ProductOrderPaymentStatus, ProductOrderStatus } from '@services/dtos'
import { useSelector } from '@state/store'
import { ProductOrderPaymentStatusLabels, ProductOrderStatusLabels } from '@utils/constants'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Badge, Box, Button, Dialog, DialogContent, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material'
import { GridToolbarContainer } from '@mui/x-data-grid-pro'
import { getProductOrders, getProductOrdersToValidate, setProductOrderFilters, setScheduledDeliveriesFilters } from '@state/reducers/productOrderReducer'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import { useEffect, useState } from 'react'
import moment from 'moment'
import { UpTransition } from '@components/animations'
import ProductOrderForm from '@components/forms/productOrder/ProductOrderForm'
import { DateInputField } from '@components/inputs/DateInputField'
import ProductOrderValidationsTable from '../ProductOrderValidationsTable'
import { Sync } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ProductScheduledDeliveriesTable from '../ProductScheduledDeliveriesTable'

const GROUPED_STATUSES = new Set([
  ProductOrderStatus.PENDING_MAIL,
  ProductOrderStatus.PENDING_CANCELLATION_MAIL,
  ProductOrderStatus.PENDING_CREDIT_NOTE_MAIL,
  ProductOrderStatus.MAIL_FAILED,
  ProductOrderStatus.CANCELLATION_MAIL_FAILED,
  ProductOrderStatus.CREDIT_NOTE_MAIL_FAILED,
])
const STATUS_OPTIONS: { key: string; label: string; statuses: ProductOrderStatus[] }[] = [
  ...(Object.entries(ProductOrderStatusLabels) as Array<[ProductOrderStatus, string]>)
    .filter(([value]) => !GROUPED_STATUSES.has(value))
    .map(([value, label]) => ({ key: value, label, statuses: [value] })),
  { key: 'PENDING_MAIL_GROUP', label: ProductOrderStatusLabels[ProductOrderStatus.PENDING_MAIL], statuses: [ProductOrderStatus.PENDING_MAIL, ProductOrderStatus.PENDING_CANCELLATION_MAIL, ProductOrderStatus.PENDING_CREDIT_NOTE_MAIL] },
  { key: 'MAIL_FAILED_GROUP', label: ProductOrderStatusLabels[ProductOrderStatus.MAIL_FAILED], statuses: [ProductOrderStatus.MAIL_FAILED, ProductOrderStatus.CANCELLATION_MAIL_FAILED, ProductOrderStatus.CREDIT_NOTE_MAIL_FAILED] },
]

const ProductOrdersTableToolbar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [overflowMenuAnchor, setOverflowMenuAnchor] = useState<HTMLElement | null>(null)
  const [productOrderFormOpen, setProductOrderFormOpen] = useState(false)
  const [scheduledDeliveriesOpen, setScheduledDeliveriesOpen] = useState(false)
  const [validationFormOpen, setValidationFormOpen] = useState(false)
  const { countProductOrdersToValidate, scheduledDeliveriesFilters } = useSelector(({ productOrder }) => ({
    countProductOrdersToValidate: productOrder.validation.items.totalCount,
    scheduledDeliveriesFilters: productOrder.scheduledDeliveries.filters
  }))

  useEffect(() => {
    dispatch(getProductOrdersToValidate())
  }, [dispatch])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const dialog = searchParams.get('dialog')
    if (dialog === 'validations') {
      setValidationFormOpen(true)
      searchParams.delete('dialog')
      navigate(
        { pathname: location.pathname, search: searchParams.toString() },
        { replace: true }
      )
    }
  }, [location.search, location.pathname, navigate])

  const { filters } = useSelector(({ productOrder }) => ({ filters: productOrder.crud.filters }))

  return (<GridToolbarContainer className="m-2 mb-0">
    <div className="flex items-center w-full">
      <div className="flex items-center space-x-2 flex-wrap flex-1 min-w-0 gap-y-2">
          <Button
            variant="outlined"
            color="secondary"
            style={{ backgroundColor: '#fef1f4' }}
            onClick={() => setProductOrderFormOpen(true)}
            startIcon={<AddShoppingCartIcon />}
          >
            Passer commande
          </Button>
          <Tooltip title="Actualiser commandes" arrow>
            <IconButton
              onClick={() => { dispatch(getProductOrders()) }}
            >
              <Sync />
            </IconButton>
          </Tooltip>
          <FormControl size="small">
            <InputLabel>Statut</InputLabel>
            <Select
              value={STATUS_OPTIONS.find(opt =>
                opt.statuses.length === (filters.statuses?.length ?? 0) &&
                opt.statuses.every(s => filters.statuses?.includes(s))
              )?.key ?? ''}
              onChange={(event: SelectChangeEvent) => {
                const key = event.target.value
                const option = STATUS_OPTIONS.find(opt => opt.key === key)
                dispatch(setProductOrderFilters({ statuses: option?.statuses }))
              }}
              style={{
                minWidth: '200px',
                marginRight: 6,
              }}
              size="small"
              label="Statut"
            >
              <MenuItem value="">
                <em>Tous</em>
              </MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Paiement</InputLabel>
            <Select
              value={filters.paymentStatus ?? ''}
              onChange={(event: SelectChangeEvent) => {
                const value = event.target.value === '' ? undefined : (event.target.value as ProductOrderPaymentStatus)
                dispatch(setProductOrderFilters({ paymentStatus: value }))
              }}
              style={{ minWidth: '150px', marginRight: 6 }}
              size="small"
              label="Paiement"
            >
              <MenuItem value=""><em>Tous</em></MenuItem>
              {(Object.entries(ProductOrderPaymentStatusLabels) as Array<[ProductOrderPaymentStatus, string]>).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="N° interne"
            onChange={(event) => {
              dispatch(setProductOrderFilters({ internalOrderNumber: event.target.value }))
            }}
            size="small"
            placeholder='Rechercher N° interne...'
          />
          <DateInputField
            value={
              filters.from ? moment(filters.from) : undefined
            }
            onChange={(date) => {
              dispatch(
                setProductOrderFilters({
                  from: date
                    ? date.startOf('day').toISOString()
                    : undefined,
                }),
              )
            }}
          />
          <Typography>au</Typography>
          <DateInputField
            value={filters.to ? moment(filters.to) : undefined}
            onChange={(date) => {
              dispatch(
                setProductOrderFilters({
                  to: date
                    ? date.endOf('day').toISOString()
                    : undefined,
                }),
              )
            }}
          />
        </div>

        <>
          <Badge 
            badgeContent={countProductOrdersToValidate} 
            color="secondary" 
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }} 
            sx={{ '& .MuiBadge-badge': { top: 8, right: 8 } }}>
            <IconButton onClick={(event) => setOverflowMenuAnchor(event.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          </Badge>

          <Menu
            anchorEl={overflowMenuAnchor}
            open={Boolean(overflowMenuAnchor)}
            onClose={() => setOverflowMenuAnchor(null)}
          >
            <MenuItem onClick={() => {
              setValidationFormOpen(true)
              setOverflowMenuAnchor(null)
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                Valider commandes
                <Badge badgeContent={countProductOrdersToValidate} color="secondary" />
              </Box>
            </MenuItem>
            <MenuItem onClick={() => {
              setScheduledDeliveriesOpen(true)
              setOverflowMenuAnchor(null)
            }}>
              Livraisons prévues
            </MenuItem>
          </Menu>
        </>
      </div>

      <Dialog
        open={productOrderFormOpen}
        onClose={() => setProductOrderFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        fullScreen
      >
        <DialogContent>
          {productOrderFormOpen && (
            <ProductOrderForm onClose={() => setProductOrderFormOpen(false)} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={scheduledDeliveriesOpen}
        onClose={() => setScheduledDeliveriesOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        fullScreen
      >
        <DialogContent>
          {scheduledDeliveriesOpen && (
            <>
              <div className="flex justify-between items-center mb-6">
                <Typography variant="h6">{'Livraisons prévues'}</Typography>
                <DateInputField
                  value={moment(scheduledDeliveriesFilters.date)}
                  onChange={(date) => {
                    dispatch(
                      setScheduledDeliveriesFilters({
                        date: date ? date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
                      }),
                    )
                  }}
                />
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setScheduledDeliveriesOpen(false)}>
                  Fermer
                </Button>
              </div>
              <div style={{ height: 'calc(100% - 80px)' }}><ProductScheduledDeliveriesTable /></div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={validationFormOpen}
        onClose={() => setValidationFormOpen(false)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
        fullScreen
      >
        <DialogContent>
          {validationFormOpen && (
            <>
              <div className="flex justify-between items-center mb-6">
                <Typography variant="h6">{'Commandes à valider'}</Typography>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setValidationFormOpen(false)}>
                  Fermer
                </Button>
              </div>
              <div style={{ height: 'calc(100% - 80px)' }}><ProductOrderValidationsTable /></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </GridToolbarContainer>
  )
}

export default ProductOrdersTableToolbar
